<script lang="ts" setup>
const props = defineProps<{
  elements: SidebarElement[];
}>();

const { isAdminSection } = useSidebar();
const { refresh, env, user, isAdminUser, clear: logout } = useAuth();
const isMobileMenuOpen = useState("isMobileMenuOpen", () => false);

const handleTitleDoubleClick = async () => {
  useToast().add({
    title: "Refreshing user...",
    description: "Please wait...",
  });
  await refresh();
};

const filteredElements = computed(() =>
  props.elements.filter((element) => !element.adminOnly || isAdminUser.value)
);

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
};

const userFullName = computed(() => {
  return `${user.value?.user.first_name} ${user.value?.user.last_name ?? ""}`;
});

const handleLogout = async () => {
  await logout();
};

// Close mobile menu when route changes
const route = useRoute();
watch(
  () => route.path,
  () => {
    isMobileMenuOpen.value = false;
  }
);

const merchantLogoUrl = computed(() => {
  if (!user.value?.merchant.logo) return null;
  return `${useRuntimeConfig().public.r2BaseUrl}/${user.value.merchant.logo}`;
});
</script>

<template>
  <div>
    <!-- Mobile Menu Button -->
    <button
      class="fixed left-4 top-4 z-50 flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 lg:hidden dark:bg-gray-800"
      @click="toggleMobileMenu"
    >
      <UIcon
        :name="isMobileMenuOpen ? 'i-heroicons-x-mark' : 'i-heroicons-bars-3'"
        class="h-6 w-6"
      />
    </button>

    <!-- Sidebar -->
    <aside
      class="fixed left-0 top-0 z-40 flex h-screen w-64 flex-col border-r border-gray-200 bg-white transition-transform duration-300 lg:translate-x-0 dark:border-gray-800 dark:bg-gray-900 pb-safe"
      :class="[isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0']"
    >
      <!-- Header Section -->
      <div class="p-4 pt-16 lg:pt-4">
        <div class="mb-6 flex items-center justify-between">
          <h1
            class="text-xl font-bold"
            @dblclick="handleTitleDoubleClick"
          >
            {{ isAdminSection ? "Administration" : "Abonne-moi" }}
            <UBadge
              v-if="env && env !== 'production'"
              :label="env"
              :color="env === 'local' ? 'gray' : 'green'"
            />
          </h1>
        </div>
      </div>

      <!-- Navigation Section (Scrollable) -->
      <div class="flex-1 overflow-y-auto p-4 pt-0">
        <UVerticalNavigation :links="filteredElements" />
      </div>

      <!-- Footer Section -->
      <div
        v-if="user"
        class="mt-auto border-t border-gray-200 p-4 dark:border-gray-800"
      >
        <div class="flex items-center gap-3">
          <UAvatar
            v-if="user.merchant.logo"
            :src="merchantLogoUrl"
            :alt="user.user.email"
            :text="user.user.email?.charAt(0)"
          />
          <div class="min-w-0 flex-1">
            <p class="truncate text-sm font-medium text-gray-900 dark:text-white">
              {{ userFullName }}
            </p>
            <p class="truncate text-xs text-gray-500 dark:text-gray-400">
              {{ user.user.email }}
            </p>
          </div>
          <UTooltip text="Déconnexion">
            <UButton
              icon="i-heroicons-arrow-right-on-rectangle"
              color="gray"
              variant="ghost"
              size="sm"
              @click="handleLogout"
            />
          </UTooltip>
        </div>
      </div>
    </aside>

    <!-- Mobile Overlay -->
    <div
      v-if="isMobileMenuOpen"
      class="fixed inset-0 z-30 bg-black bg-opacity-50 lg:hidden"
      @click="toggleMobileMenu"
    />
  </div>
</template>
